import { useState } from 'react';
import { ArrowRight, CheckCircle, BarChart3, Users, Calendar, Briefcase, Zap, Star, Quote } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ContactFormModal } from '../components/ContactFormModal';
import { useSiteContent } from '../contexts/SiteContentContext';
import { SEOHead, BusinessStructuredData } from '../components/SEOHead';

const ICON_MAP = { BarChart3, Briefcase, Users, Calendar };
const CTA_MAP = { BarChart3: 'Book Assessment', Briefcase: 'Join Program', Users: 'Apply Now', Calendar: 'Book Workshop' };
const TYPE_MAP = { Calendar: 'workshop' };

export const BusinessPage = () => {
  const pageRef = useScrollReveal();
  const { get, testimonials } = useSiteContent();
  const [modal, setModal] = useState({ open: false, type: 'business' });

  const openForm = (type) => setModal({ open: true, type });
  const closeForm = () => setModal({ open: false, type: 'business' });

  const hero = get('business_hero');
  const solution = get('business_solution');
  const programs = get('programs');
  const bizTestimonials = testimonials('business');

  const outcomes = [
    'Improved salon revenue and profitability',
    'Streamlined booking and operations',
    'Stronger team performance and retention',
    'Better client experience and growth',
  ];

  return (
    <main className="pt-24" ref={pageRef}>
      <SEOHead path="/business" />
      <BusinessStructuredData />
      <ContactFormModal isOpen={modal.open} onClose={closeForm} inquiryType={modal.type} sourcePage="business" />

      {/* Hero Section */}
      <section className="section-padding bg-layered" data-testid="business-hero">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="reveal mb-6">
                <img src="/images/DIS-logo.svg" alt="Diverse Intelligence Systems" className="w-48 h-48 md:w-56 md:h-56" data-testid="business-dis-logo" />
              </div>
              <span className="reveal badge-coral mb-6">{hero.badge_text}</span>
              <h1 className="reveal reveal-delay-1 heading-xl mb-6">{hero.heading_line1}<span className="block text-highlight">{hero.heading_line2}</span></h1>
              <p className="reveal reveal-delay-2 body-text max-w-lg mb-8">{hero.description}</p>
              <div className="reveal reveal-delay-3 flex flex-wrap gap-4">
                <button onClick={() => openForm('business')} className="btn-primary arrow-hover" data-testid="business-hero-cta">Get Started <ArrowRight className="w-4 h-4" /></button>
                <a href="#programs" className="btn-outline">View Programs</a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="reveal-image aspect-[3/4] image-frame rounded-2xl"><img src="/images/SalonTeam/Website/BUSINESS_DIS/Salons/SALON_team_working.JPG" alt="Salon team at work" className="w-full h-full object-cover object-[center_40%]" /></div>
              <div className="reveal-image reveal-delay-2 aspect-[3/4] image-frame rounded-2xl mt-8"><img src="/images/SalonTeam/Website/BUSINESS_DIS/Salons/Salon exterior.jpg" alt="Salon exterior" className="w-full h-full object-cover object-[center_50%]" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding bg-white" data-testid="business-problem">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="reveal overline-text mb-4 block">The Challenge</span>
              <h2 className="reveal reveal-delay-1 heading-lg">Sound Familiar?</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['Overwhelmed managing clients, staff schedules, and daily salon operations all by yourself.',
                'Inconsistent bookings and revenue making it impossible to plan ahead or hire help.',
                'Difficulty building and retaining a strong, reliable salon team.',
                'Stuck behind the chair with no time to actually grow your business.',
              ].map((text, i) => (
                <div key={i} className={`reveal ${i > 0 ? `reveal-delay-${i}` : ''} feature-card`}>
                  <Zap className="w-8 h-8 text-[#E84993] mb-4" /><p className="body-text">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section-padding bg-[#FDF5F0]" data-testid="business-solution">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="reveal-image image-frame rounded-3xl aspect-[4/3] order-2 lg:order-1"><img src="/images/SalonTeam/Website/PERSONAL_BOY/Events/IMG_2463.JPG" alt="Salon team training" className="w-full h-full object-cover object-[center_30%]" /></div>
            <div className="order-1 lg:order-2">
              <div className="reveal mb-5">
                <img src="/images/DIS-logo.svg" alt="Diverse Intelligence Systems" className="w-40 h-40 md:w-48 md:h-48" data-testid="solution-dis-logo" />
              </div>
              <span className="reveal overline-text mb-4 block">{solution.overline}</span>
              <h2 className="reveal reveal-delay-1 heading-lg mb-6">{solution.heading}</h2>
              <div className="reveal reveal-delay-1 divider-accent mb-6" />
              <p className="reveal reveal-delay-2 body-text mb-6">{solution.description1}</p>
              <p className="reveal reveal-delay-2 body-text mb-8">{solution.description2}</p>
              <button onClick={() => openForm('business')} className="reveal reveal-delay-3 btn-primary arrow-hover" data-testid="business-solution-cta">Learn More <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="section-padding bg-white" data-testid="business-offers">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="reveal overline-text mb-4 block">How We Work Together</span>
            <h2 className="reveal reveal-delay-1 heading-lg">Programs for Salon Owners</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(Array.isArray(programs) ? programs : []).map((prog, index) => {
              const Icon = ICON_MAP[prog.icon] || Briefcase;
              const formType = TYPE_MAP[prog.icon] || 'business';
              const ctaText = CTA_MAP[prog.icon] || 'Learn More';
              return (
                <div key={index} className={`reveal ${index > 0 ? `reveal-delay-${index}` : ''} feature-card group`} data-testid={`offer-card-${index}`}>
                  <div className="w-14 h-14 bg-[#FFE8F0] rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#E84993] transition-colors duration-300">
                    <Icon className="w-7 h-7 text-[#E84993] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="heading-md mb-3">{prog.title}</h3>
                  <p className="body-text mb-6">{prog.description}</p>
                  <button onClick={() => openForm(formType)} className="btn-ghost arrow-hover" data-testid={`offer-cta-${index}`}>{ctaText} <ArrowRight className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Business Testimonials */}
      <section className="py-16 md:py-20 bg-[#FDF5F0]" data-testid="business-testimonials">
        <div className="container-padding">
          <div className="text-center mb-12">
            <span className="reveal overline-text mb-3 block">Success Stories</span>
            <h2 className="reveal reveal-delay-1 heading-lg">What Salon Owners Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {bizTestimonials.map((t, i) => (
              <div key={i} className={`reveal ${i > 0 ? `reveal-delay-${i + 1}` : ''} bg-white p-8 rounded-2xl border border-[#E8DDD5] relative`}>
                <Quote className="w-8 h-8 text-[#E84993]/30 absolute top-6 right-6" />
                <div className="flex gap-1 mb-4">{[...Array(t.rating || 5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#E84993] text-[#E84993]" />)}</div>
                <p className="text-[#4A4A4A] mb-6 leading-relaxed">"{t.quote}"</p>
                <div><p className="font-medium text-[#1A1A1A]">{t.name}</p><p className="text-sm text-[#7A7A7A]">{t.role}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="section-padding bg-layered" data-testid="business-success">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="reveal overline-text mb-4 block">Real Results</span>
              <h2 className="reveal reveal-delay-1 heading-lg mb-6">Built for Salon Success</h2>
              <p className="reveal reveal-delay-2 body-text mb-8">Natasha works directly with salon owners and beauty professionals—delivering hands-on training, strategic sessions, and actionable coaching designed for your industry.</p>
              <ul className="space-y-4 mb-8">
                {outcomes.map((outcome, index) => (
                  <li key={index} className={`reveal reveal-delay-${index + 1} flex items-center gap-4`}>
                    <div className="w-8 h-8 bg-[#E84993] rounded-full flex items-center justify-center flex-shrink-0"><CheckCircle className="w-4 h-4 text-white" /></div>
                    <span className="body-text">{outcome}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => openForm('workshop')} className="reveal btn-outline arrow-hover" data-testid="workshop-cta">Book a Workshop <ArrowRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="reveal-image aspect-[3/4] image-frame rounded-2xl"><img src="/images/SalonTeam/Website/BUSINESS_DIS/Salons/IMG_1622.JPG" alt="Salon consultation" className="w-full h-full object-cover object-[center_30%]" /></div>
              <div className="reveal-image reveal-delay-2 aspect-[3/4] image-frame rounded-2xl mt-8"><img src="/images/SalonTeam/Website/PERSONAL_BOY/Events/1U6A6472.jpg" alt="Beauty professional team" className="w-full h-full object-cover object-[center_30%]" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Gallery */}
      <section className="section-padding bg-white" data-testid="business-events">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="reveal overline-text mb-4 block">In Action</span>
            <h2 className="reveal reveal-delay-1 heading-lg">Workshops & Industry Events</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['/images/Events/IMG_7592.JPG', '/images/Events/IMG_6389.JPG', '/images/Events/IMG_7721.JPG', '/images/Events2/EVENT 2/224A2232.jpg'].map((src, i) => (
              <div key={i} className={`reveal-image ${i > 0 ? `reveal-delay-${i}` : ''} aspect-square image-frame img-zoom rounded-2xl`}>
                <img src={src} alt="Event" className={`w-full h-full object-cover ${i === 3 ? 'object-[center_30%]' : ''}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Red Carpet Girl® */}
      <section className="section-padding bg-cream" data-testid="red-carpet-girl-section">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="reveal bg-white rounded-3xl overflow-hidden border border-[#F0E6F6] hover:border-[#E84993]/20 transition-all duration-500 hover:shadow-xl hover:shadow-[#E84993]/5">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 relative overflow-hidden">
                  <div className="aspect-[4/3] lg:aspect-auto lg:h-full">
                    <img src="/images/natasha-rcg-group.jpg" alt="Natasha Simone — Red Carpet Girl leadership" className="w-full h-full object-cover object-left" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3A1F3D]/50 to-transparent lg:bg-gradient-to-r" />
                  </div>
                </div>
                <div className="lg:col-span-3 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span className="overline-text mb-3 block">Leadership & Empowerment</span>
                  <h2 className="text-2xl md:text-3xl font-medium mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Red Carpet Girl<sup>&reg;</sup>
                  </h2>
                  <p className="text-lg text-[#E84993] italic mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Where Confidence Meets Exceptional Conduct
                  </p>
                  <div className="divider-accent mb-5" />
                  <p className="body-text mb-8">
                    Through mentorship, leadership experiences, etiquette training, and confidence development, Natasha Simone actively contributes to empowering young women to lead with grace, purpose, confidence, and excellence.
                  </p>
                  <a href="https://www.avenuesofexcellence.com/" target="_blank" rel="noopener noreferrer" className="btn-primary arrow-hover self-start" data-testid="rcg-cta">
                    Discover More <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E84993 0%, #B565CC 50%, #9B4FBF 100%)' }} data-testid="business-final-cta">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="reveal text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Ready to Scale Your Salon?</h2>
            <p className="reveal reveal-delay-1 text-white/80 mb-10">Let's discuss how Diverse Intelligence Systems can help you build the beauty business you've always dreamed of.</p>
            <button onClick={() => openForm('booking_call')} className="reveal reveal-delay-2 inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#E84993] text-xs font-medium tracking-[0.15em] uppercase rounded-full hover:-translate-y-0.5 transition-all hover:shadow-lg arrow-hover" data-testid="business-book-call">Book a Free Call <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BusinessPage;
